import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import PricingTable from "@/components/shared/PricingTable";
import FAQSection from "@/components/home/FAQSection";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { Rocket, Cpu, Shield, Headphones, Database, Gauge } from "lucide-react";

const packages = [
  {
    name: "Starter",
    originalPrice: "698",
    price: "349",
    period: "/month",
    description: "Entry-level turbo power",
    features: [
      "5 GB NVMe SSD",
      "5 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "LiteSpeed Cache",
      "Dedicated Resources",
      "Priority Queue",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-turbo-hosting/starter",
  },
  {
    name: "Advanced",
    originalPrice: "1,198",
    price: "599",
    period: "/month",
    description: "For small businesses",
    features: [
      "10 GB NVMe SSD",
      "10 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "LiteSpeed Cache",
      "Dedicated Resources",
      "Priority Queue",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-turbo-hosting/advanced",
  },
  {
    name: "Business",
    originalPrice: "1,698",
    price: "849",
    period: "/month",
    description: "Maximum speed",
    features: [
      "20 GB NVMe SSD",
      "20 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "LiteSpeed Cache",
      "Dedicated Resources",
      "Priority Queue",
    ],
    popular: true,
    orderLink: "https://my.webhostbuybd.com/store/bdix-turbo-hosting/business",
  },
  {
    name: "Professional",
    originalPrice: "2,198",
    price: "1,099",
    period: "/month",
    description: "Professional grade",
    features: [
      "50 GB NVMe SSD",
      "50 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "Redis Cache",
      "Dedicated Resources",
      "Premium Support",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-turbo-hosting/professional",
  },
  {
    name: "Enterprise",
    originalPrice: "2,698",
    price: "1,349",
    period: "/month",
    description: "Enterprise solution",
    features: [
      "100 GB NVMe SSD",
      "100 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "Redis Cache",
      "Dedicated Resources",
      "Premium Support",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-turbo-hosting/enterprise",
  },
  {
    name: "Ultimate",
    originalPrice: "3,198",
    price: "1,599",
    period: "/month",
    description: "Maximum everything",
    features: [
      "200 GB NVMe SSD",
      "200 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "Redis Cache",
      "Dedicated Resources",
      "Premium Support",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-turbo-hosting/ultimate",
  },
];

const features = [
  { icon: Rocket, title: "20x Faster", description: "Turbo BDIX servers" },
  { icon: Cpu, title: "Dedicated CPU", description: "Isolated resources" },
  { icon: Database, title: "Redis Cache", description: "In-memory caching" },
  { icon: Gauge, title: "LiteSpeed", description: "QUIC enabled" },
  { icon: Shield, title: "DDoS Protection", description: "Enterprise security" },
  { icon: Headphones, title: "Priority Support", description: "Skip the queue" },
];

const turboFaqs = [
  {
    question: "What is BDIX hosting and why is it important?",
    answer: "BDIX (Bangladesh Internet Exchange) hosting provides ultra-fast connectivity within Bangladesh by routing traffic through local exchange points. This results in significantly lower latency and faster page load times for visitors within Bangladesh, making it ideal for local businesses and websites targeting Bangladeshi audiences.",
  },
  {
    question: "Do you offer free website migration?",
    answer: "Yes! We offer completely free website migration for all our hosting plans. Our expert team will migrate your website, databases, emails, and all files from your current host to our servers with zero downtime.",
  },
  {
    question: "What control panel do you use?",
    answer: "We use cPanel, the industry's most popular and user-friendly control panel. It provides an intuitive interface for managing your website, emails, databases, files, and more. We also offer one-click installers for WordPress, Joomla, and 400+ other applications.",
  },
  {
    question: "What is your uptime guarantee?",
    answer: "We guarantee 99.99% uptime for all our hosting services, backed by our Service Level Agreement (SLA). Our state-of-the-art datacenter infrastructure, redundant power systems, and 24/7 monitoring ensure your website stays online.",
  },
  {
    question: "Do you provide daily backups?",
    answer: "Yes, we perform automated daily backups of all hosting accounts. Backups are stored on separate servers for added security. You can also create manual backups anytime through cPanel or request restoration from our support team.",
  },
  {
    question: "How fast is your customer support?",
    answer: "Our customer support team is available 24/7 via live chat, phone, and ticket system. Average response time for live chat is under 2 minutes, and most tickets are resolved within 1 hour. Our support team consists of certified hosting experts.",
  },
];

const TurboHosting = () => {
  return (
    <Layout>
       <SEOHead
         title="Fastest BDIX Turbo Hosting Bangladesh | 20x Faster Turbo NVMe SSD + LiteSpeed"
         description="Fastest BDIX web hosting Bangladesh - 20x faster turbo hosting with LiteSpeed, dedicated CPU &amp; Redis cache. NVMe SSD hosting from ৳349/month. Best hosting provider in Bangladesh for high-traffic sites. Turbo hosting with BDIX cloud hosting."
         canonical="/bdix-turbo-hosting"
         type="product"
         keywords="fastest BDIX web hosting, turbo hosting, NVMe SSD hosting, bdix cloud hosting, LiteSpeed hosting, fast web hosting Bangladesh, premium hosting Bangladesh, bdix turbo hosting, bdix shared hosting, affordable web hosting Bangladesh, SSD hosting Bangladesh, reliable web hosting Bangladesh, web hosting bd, premium web hosting Bangladesh"
       />
      <SchemaMarkup
        type="product"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "BDIX Turbo Hosting", url: "/bdix-turbo-hosting" },
        ]}
        faqs={turboFaqs}
        products={packages.map((p) => ({
          name: `${p.name} - BDIX Turbo Hosting`,
          description: `${p.description}. ${p.features[0]} NVMe SSD, LiteSpeed Cache, Dedicated Resources, Free SSL, BDIX optimized.`,
          price: p.price,
          orderLink: p.orderLink,
          ratingValue: p.popular ? "4.9" : "4.8",
          reviewCount: p.popular ? "203" : "147",
        }))}
        pageName="BDIX Turbo Hosting Bangladesh - 20x Faster NVMe SSD"
        pageDescription="Fastest BDIX turbo hosting with 20x faster LiteSpeed servers, dedicated CPU, and NVMe SSD from ৳349/month."
      />
      <PageHero
        badge="Fastest BDIX Turbo Hosting"
        title="Fastest BDIX Turbo Hosting in Bangladesh"
        subtitle="20x faster NVMe SSD servers with dedicated resources, LiteSpeed cache, and enterprise DDoS protection."
      />

      {/* Features Strip */}
      <section className="py-12 bg-muted/30 border-b border-border" aria-label="BDIX Turbo Hosting Features">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground text-sm">{feature.title}</div>
                  <div className="text-xs text-muted-foreground">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingTable
        packages={packages}
        title="Choose Your BDIX Turbo Hosting Plan"
        subtitle="All BDIX turbo plans include LiteSpeed cache, dedicated resources, and priority support. Affordable hosting price in Bangladesh."
      />

      <FAQSection />
      <PartnersSection />
    </Layout>
  );
};

export default TurboHosting;
